import { useCallback, useEffect, useState } from "react";

import {
  checkActiveUnlock,
  createUnlock,
  revokeUnlock,
} from "~/services/clientCreditUnlockService";
import { useProfileStore } from "~/store/profileStore";
import {
  ClientCreditUnlockWithProfile,
  UnlockType,
} from "~/types/clientCreditUnlock";

interface UseClientCreditUnlockResult {
  activeUnlock: ClientCreditUnlockWithProfile | null;
  isUnlocked: boolean;
  canUnlock: boolean;
  isLoading: boolean;
  isUnlocking: boolean;
  unlock: (reason?: string) => Promise<boolean>;
  revoke: () => Promise<boolean>;
  refetch: () => Promise<void>;
}

export function useClientCreditUnlock(
  clientId: string,
  unlockType: UnlockType
): UseClientCreditUnlockResult {
  const { role, id: userId } = useProfileStore();
  const canUnlock = role === "superadmin" || role === "cobranza";

  const [activeUnlock, setActiveUnlock] =
    useState<ClientCreditUnlockWithProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUnlocking, setIsUnlocking] = useState(false);

  const fetchActiveUnlock = useCallback(async () => {
    if (!clientId) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const unlock = await checkActiveUnlock(clientId, unlockType);
      setActiveUnlock(unlock);
    } catch (error) {
      console.error("Error fetching active unlock:", error);
      setActiveUnlock(null);
    } finally {
      setIsLoading(false);
    }
  }, [clientId, unlockType]);

  useEffect(() => {
    fetchActiveUnlock();
  }, [fetchActiveUnlock]);

  const unlock = async (reason?: string): Promise<boolean> => {
    if (!canUnlock || !userId || !clientId) {
      return false;
    }

    setIsUnlocking(true);
    try {
      const newUnlock = await createUnlock(
        clientId,
        unlockType,
        userId,
        reason
      );
      setActiveUnlock(newUnlock);
      return true;
    } catch (error) {
      console.error("Error creating unlock:", error);
      return false;
    } finally {
      setIsUnlocking(false);
    }
  };

  const revoke = async (): Promise<boolean> => {
    if (!canUnlock || !userId || !activeUnlock) {
      return false;
    }

    setIsUnlocking(true);
    try {
      await revokeUnlock(activeUnlock.id, userId);
      setActiveUnlock(null);
      return true;
    } catch (error) {
      console.error("Error revoking unlock:", error);
      return false;
    } finally {
      setIsUnlocking(false);
    }
  };

  return {
    activeUnlock,
    isUnlocked: !!activeUnlock,
    canUnlock,
    isLoading,
    isUnlocking,
    unlock,
    revoke,
    refetch: fetchActiveUnlock,
  };
}
